import { useLocation } from "react-router";
import { useRun } from "@/app/providers";
import { reauthenticate, SessionExpiredError } from "@/shared/api";
import { collectDiagnostics, logSnapshot } from "@/shared/diagnostics";
import { useAppConfig } from "@/modules/overview";
import { useSubmitFeedback } from "./api";
import { FeedbackDialog, type FeedbackFormValues } from "./components/FeedbackDialog";

interface FeedbackReporterProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Everything behind the report dialog: the mutation, and the diagnostics.
 *
 * It holds no open/closed state of its own. The trigger lives in the app shell's
 * navigation, which is rendered by `AppLayout`, so `AppLayout` owns the flag and
 * passes it down — one owner rather than a context for a single boolean.
 *
 * The diagnostics are gathered **here, at the moment of submit**, from the route
 * the reporter is standing on and the reconciliation they have loaded. Doing it
 * any earlier would describe a screen they have since left; doing it inside the
 * dialog would mean the dialog had to know about routing and run state. It knows
 * about neither — it collects what the reporter types and hands it back.
 */
export function FeedbackReporter({ isOpen, onClose }: FeedbackReporterProps) {
  const { pathname } = useLocation();
  const { result } = useRun();
  const config = useAppConfig();
  const submit = useSubmitFeedback();

  const feedback = config.data?.feedback;
  if (!feedback?.enabled) return null;

  const send = (values: FeedbackFormValues) => {
    submit.mutate({ ...values, diagnostics: collectDiagnostics(pathname, result) });
  };

  const close = () => {
    onClose();
    // Clears a stale error or receipt, so reopening starts from where they are
    // now rather than from the last thing that happened.
    submit.reset();
  };

  return (
    <FeedbackDialog
      isOpen={isOpen}
      limits={feedback}
      isSending={submit.isPending}
      error={
        submit.error && !(submit.error instanceof SessionExpiredError)
          ? submit.error.message
          : null
      }
      isSessionExpired={submit.error instanceof SessionExpiredError}
      receipt={submit.data ?? null}
      userEmail={config.data?.user_email ?? null}
      hasRun={result !== null}
      logCount={logSnapshot().length}
      onSubmit={send}
      onClose={close}
      onStartAnother={submit.reset}
      onReauthenticate={reauthenticate}
    />
  );
}
